<script lang="ts" setup>
import { z } from 'zod'

const { t } = useI18n()
const adminStore = useAdminStore()
const authStore = useAuthStore()
const localePath = useLocalePath()
const router = useRouter()
const route = useRoute()

const impersonating = ref(false)
const impersonatingAdmin = ref(false)
const form = reactive({ impersonate: { email: '' } })

const validation = {
  impersonate: z.object({
    email: z.string()
      .email({ message: t('validation.email') })
      .max(255, { message: t('validation.max', { length: 255 }) }),
  }),
}

// Mounted
onMounted(async () => {
  // Impersonate user when query param is present
  if (route.query.impersonate) {
    await impersonate(`${route.query.impersonate}`)
  }
})

async function impersonate(email: string) {
  try {
    if (email) {
      impersonating.value = true

      await adminStore.impersonate({ email })

      await router.push(localePath('settings-profile-and-security'))

      location.reload()
    }
  }
  catch (error) {
    console.error(error)
  }

  impersonating.value = false
}

function onImpersonateAdmin() {
  impersonatingAdmin.value = true

  authStore.setOpaqueToken(localStorage.getItem('adminOpaqueToken'))
  location.reload()

  impersonatingAdmin.value = false
}

async function onImpersonateUser() {
  const email = form.impersonate.email.trim().toLowerCase()

  if (!impersonating.value && email) {
    await impersonate(email)
  }
}
</script>

<template>
  <LayoutBar color="white">
    <UForm
      class="flex items-start gap-2"
      :schema="validation.impersonate"
      :state="form.impersonate"
      @submit="onImpersonateUser"
    >
      <UFormGroup name="email">
        <UInput
          v-model="form.impersonate.email"
          autocomplete="off"
          autofocus
          :icon="getIcon('email')"
          :placeholder="t('placeholder')"
          size="md"
          type="email"
        />
      </UFormGroup>

      <UButton
        :icon="getIcon('impersonate')"
        :label="t('impersonate')"
        :loading="impersonating"
        size="md"
        type="submit"
      />

      <UButton
        color="indigo"
        :icon="getIcon('admin')"
        :label="t('admin')"
        :loading="impersonatingAdmin"
        size="md"
        type="button"
        variant="outline"
        @click="onImpersonateAdmin"
      />
    </UForm>
  </LayoutBar>
</template>

<i18n lang="json">
{
  "en": {
    "admin": "Admin",
    "impersonate": "Impersonate",
    "placeholder": "Email address"
  },
  "es": {
    "admin": "Admin",
    "impersonate": "Impersonar",
    "placeholder": "Email"
  },
  "fr": {
    "admin": "Admin",
    "impersonate": "Imiter",
    "placeholder": "Adresse email"
  }
}
</i18n>
